import { useMemo, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { useBudget } from '../store/BudgetContext';
import { theme } from '../theme';
import { Icon, categoryIconName } from '../components/Icon';
import { CategoryPlanPad } from '../components/CategoryPlanPad';
import { HorizonSwitcher } from '../components/HorizonSwitcher';
import { fmtK, fmtMoney } from '../utils/format';
import { HorizonId, getWindow, isInWindow } from '../utils/horizons';
import { buildMoM, buildStacked } from '../utils/trends';
import { SavingsTab } from './SavingsTab';

const W = 360;

/** Число без валюты: «8 400». */
const fmtNum = (n: number) => fmtMoney(n, '').trim();

/** Каталожные развороты вкладки «Месяц» (открыт один за раз). */
type TrendSection = 'limits' | 'mom' | 'six';

export function Trends() {
  const { viewData: data } = useBudget();
  const [h, setH] = useState<HorizonId>(1);

  const period = useMemo(() => {
    if (!data) return null;
    const statsSince = data.settings.stats_since;
    const win = getWindow(h, { statsSince });
    const catByName = new Map(data.categories.map((c) => [c.name, c] as const));
    let income = 0;
    let expense = 0;
    // расход по группам → категориям
    const byGroup = new Map<string, { total: number; cats: Map<string, number> }>();
    for (const op of data.operations) {
      if (!isInWindow(op.date, win)) continue;
      const cat = catByName.get(op.category);
      const type = cat?.type ?? op.type;
      if (type === 'Доход') income += op.amount;
      else if (type === 'Расход') {
        expense += op.amount;
        const gname = cat?.grp || 'Прочее';
        const g = byGroup.get(gname) ?? { total: 0, cats: new Map() };
        g.total += op.amount;
        g.cats.set(op.category, (g.cats.get(op.category) || 0) + op.amount);
        byGroup.set(gname, g);
      }
    }
    const planOf = (name: string) => (catByName.get(name)?.plan_month || 0) * win.planMultiplier;
    const groups = Array.from(byGroup.entries())
      .map(([name, g]) => {
        const cats = Array.from(g.cats.entries())
          .map(([cn, t]) => ({ name: cn, total: t, share: g.total > 0 ? t / g.total : 0, plan: planOf(cn) }))
          .sort((a, b) => b.total - a.total);
        return {
          name,
          total: g.total,
          share: expense > 0 ? g.total / expense : 0,
          plan: cats.reduce((s, c) => s + c.plan, 0),
          cats,
        };
      })
      .sort((a, b) => b.total - a.total);
    return { income, expense, saved: income - expense, groups };
  }, [data, h]);

  // этот vs прошлый календарный месяц по группам (для slope)
  const momGroups = useMemo(() => {
    if (!data) return [];
    const catByName = new Map(data.categories.map((c) => [c.name, c] as const));
    const now = new Date();
    const cur = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const pd = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const prev = `${pd.getFullYear()}-${String(pd.getMonth() + 1).padStart(2, '0')}`;
    const m = new Map<string, { cur: number; prev: number }>();
    for (const op of data.operations) {
      const cat = catByName.get(op.category);
      if ((cat?.type ?? op.type) !== 'Расход') continue;
      const g = cat?.grp || 'Прочее';
      const e = m.get(g) ?? { cur: 0, prev: 0 };
      if (op.date.startsWith(cur)) e.cur += op.amount;
      else if (op.date.startsWith(prev)) e.prev += op.amount;
      m.set(g, e);
    }
    return Array.from(m.entries())
      .map(([name, v]) => ({ name, ...v }))
      .filter((g) => g.cur > 0 || g.prev > 0)
      .sort((a, b) => b.cur - a.cur);
  }, [data]);

  const mom = useMemo(() => (data ? buildMoM(data) : null), [data]);
  const stacked = useMemo(() => (data ? buildStacked(data) : []), [data]);

  // тинты «6 месяцев»: группы по сумме за окно → от тёмного к светлому (inkTints)
  const sixColors = useMemo(() => {
    const totals = new Map<string, number>();
    for (const m of stacked) for (const s of m.segments) totals.set(s.name, (totals.get(s.name) || 0) + s.value);
    const tints = theme.colors.inkTints;
    const map = new Map<string, string>();
    Array.from(totals.entries())
      .sort((a, b) => b[1] - a[1])
      .forEach(([name], i) => map.set(name, tints[Math.min(i, tints.length - 1)]));
    return map;
  }, [stacked]);

  const currency = data?.settings.currency ?? '₽';
  const [tab, setTab] = useState<'month' | 'savings'>('month');
  const [openG, setOpenG] = useState<Set<string>>(new Set());
  const [openSec, setOpenSec] = useState<TrendSection | null>(null);
  const toggleSec = (s: TrendSection) => setOpenSec((cur) => (cur === s ? null : s));

  // выводы-резюме для строк-оглавления
  const limitCats = useMemo(
    () => (period ? period.groups.flatMap((g) => g.cats).filter((c) => c.plan > 0) : []),
    [period],
  );
  const limitOver = limitCats.filter((c) => c.total > c.plan).length;
  const limitFree = limitCats.reduce((s, c) => s + Math.max(0, c.plan - c.total), 0);
  const limitsSummary = !limitCats.length
    ? 'не заданы'
    : limitOver > 0
      ? `${limitOver} превышен · свободно ${fmtNum(limitFree)}`
      : 'все в норме';

  const momDelta = mom && mom.prev > 0 ? ((mom.cur - mom.prev) / mom.prev) * 100 : null;
  const momSummary = mom
    ? momDelta == null
      ? fmtNum(mom.cur)
      : momDelta <= 0
        ? `−${Math.abs(momDelta).toFixed(0)}% — темп лучше`
        : `+${momDelta.toFixed(0)}% — темп выше`
    : '';
  const momColor = momDelta == null ? theme.colors.sub : momDelta <= 0 ? theme.colors.green : theme.colors.accent;

  const sixMonths = stacked.filter((m) => m.total > 0);
  const sixAvg = sixMonths.length > 0 ? sixMonths.reduce((s, m) => s + m.total, 0) / sixMonths.length : 0;

  return (
    <div style={{ padding: '26px 22px 100px' }}>
      <span className="caption">Аналитика</span>
      <h1 style={{ margin: '4px 0 18px', fontSize: 30, fontWeight: 400 }}>Тренды</h1>

      {/* сегменты */}
      <div style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${theme.colors.line}`, marginBottom: 4 }}>
        {([['month', 'Месяц'], ['savings', 'Накопления']] as const).map(([id, label]) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            style={{
              flex: 1,
              padding: '10px 0',
              background: 'transparent',
              border: 'none',
              borderBottom: `2px solid ${tab === id ? theme.colors.ink : 'transparent'}`,
              color: tab === id ? theme.colors.ink : theme.colors.sub,
              fontFamily: theme.fontDisplay,
              fontSize: 17,
              cursor: 'pointer',
              marginBottom: -1,
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'month' && (
        <>
          {/* горизонт + остаток периода — служебный блок без каталожного номера */}
          {period && (
            <div style={{ marginTop: 18 }}>
              <HorizonSwitcher value={h} onChange={setH} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
                <span style={{ fontSize: 14, color: theme.colors.sub }}>Остаток</span>
                <span className="display" style={{ fontSize: 32, color: period.saved >= 0 ? theme.colors.ink : theme.colors.accent }}>
                  {period.saved >= 0 ? '' : '−'}{fmtMoney(Math.abs(period.saved), currency)}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, color: theme.colors.sub }}>
                <span>доход {fmtMoney(period.income, currency)}</span>
                <span>расход {fmtMoney(period.expense, currency)}</span>
              </div>
            </div>
          )}

          {/* обложка разворота: инсайт + treemap, без номера */}
          {period && period.groups.length > 0 && (
            <div style={{ margin: '26px 0 20px' }}>
              <SpendInsight groups={period.groups} total={period.expense} mom={momGroups} currency={currency} />
              <Treemap groups={period.groups} currency={currency} open={openG} setOpen={setOpenG} />
            </div>
          )}

          {/* оглавление 01–03: свёрнутые строки, открыта одна за раз */}
          {period && (
            <>
              <TocRow n="01" title="Лимиты" summary={limitsSummary} onClick={() => toggleSec('limits')} />
              {openSec === 'limits' && (
                <div className="rise" style={{ padding: '0 0 14px' }}>
                  <HealthLimits groups={period.groups} />
                </div>
              )}
            </>
          )}

          {mom && (
            <>
              <TocRow
                n="02"
                title={`К ${mom.day}-му числу`}
                summary={momSummary}
                summaryColor={momColor}
                onClick={() => toggleSec('mom')}
              />
              {openSec === 'mom' && (
                <div className="rise" style={{ padding: '0 0 14px' }}>
                  <MoMBars cur={mom.cur} prev={mom.prev} curLabel={mom.curLabel} prevLabel={mom.prevLabel} currency={currency} />
                </div>
              )}
            </>
          )}

          {stacked.some((m) => m.total > 0) && (
            <>
              <TocRow n="03" title="6 месяцев" summary={`в среднем ${fmtK(sixAvg)}/мес`} onClick={() => toggleSec('six')} />
              {openSec === 'six' && (
                <div className="rise" style={{ padding: '0 0 14px' }}>
                  <Stacked months={stacked} colors={sixColors} />
                  <Legend colors={sixColors} />
                </div>
              )}
            </>
          )}
        </>
      )}

      {tab === 'savings' && data && <SavingsTab data={data} currency={currency} />}
    </div>
  );
}
/**
 * Строка-оглавление каталожного разворота: hairline сверху, номер Fraunces 12,
 * заголовок Fraunces WONK:1 18, вывод-резюме 12px справа. Тап раскрывает секцию.
 */
function TocRow({
  n, title, summary, summaryColor, onClick,
}: {
  n: string; title: string; summary: ReactNode; summaryColor?: string; onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%',
        textAlign: 'left',
        background: 'transparent',
        border: 'none',
        borderTop: `1px solid ${theme.colors.line}`,
        padding: '14px 0 12px',
        fontFamily: 'inherit',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'baseline',
        gap: 10,
      }}
    >
      <span style={{ fontFamily: theme.fontDisplay, fontVariationSettings: "'opsz' 144, 'WONK' 0", fontSize: 12, color: theme.colors.sub }}>
        {n}
      </span>
      <span style={{ fontFamily: theme.fontDisplay, fontWeight: 420, fontVariationSettings: "'opsz' 144, 'WONK' 1", fontSize: 18, color: theme.colors.ink }}>
        {title}
      </span>
      <span style={{ marginLeft: 'auto', fontSize: 12, color: summaryColor ?? theme.colors.sub, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>
        {summary}
      </span>
    </button>
  );
}

interface SpendCat { name: string; total: number; share: number; plan: number }
interface SpendGroup {
  name: string;
  total: number;
  share: number;
  plan: number;
  cats: SpendCat[];
}

const TINTS = theme.colors.inkTints;

/** Текст-вывод: что доминировало + сравнение с прошлым. */
function SpendInsight({
  groups, total, mom, currency,
}: {
  groups: SpendGroup[]; total: number; mom: { name: string; cur: number; prev: number }[]; currency: string;
}) {
  if (!groups.length || total <= 0) return null;
  const top = groups[0];
  const share = Math.round((top.total / total) * 100);
  const shareWord = share >= 45 ? 'почти половина' : share >= 30 ? 'треть' : `${share}%`;
  // самый выросший против прошлого месяца
  const prevBy = new Map(mom.map((m) => [m.name, m.prev]));
  let risen: { name: string; pct: number } | null = null;
  for (const g of groups) {
    const prev = prevBy.get(g.name) ?? 0;
    if (prev > 0) {
      const pct = Math.round(((g.total - prev) / prev) * 100);
      if (pct >= 15 && (!risen || pct > risen.pct)) risen = { name: g.name, pct };
    }
  }
  return (
    <div style={{ marginBottom: 14 }}>
      <h3
        style={{
          fontFamily: theme.fontDisplay,
          fontWeight: 420,
          fontVariationSettings: "'opsz' 144, 'WONK' 1",
          fontSize: 25,
          lineHeight: 1.3,
          margin: 0,
        }}
      >
        Больше всего — на {top.name}: {fmtMoney(top.total, currency)}, это {shareWord} всех трат.
      </h3>
      {risen && (
        <div style={{ fontSize: 14, color: theme.colors.sub, marginTop: 8, fontStyle: 'italic' }}>
          {risen.name} — на {risen.pct}% больше обычного ↑
        </div>
      )}
    </div>
  );
}

/** Картина «куда ушло»: плитки-treemap, площадь = доля, тинты чернил. Тап — раскрыть категории. */
function Treemap({
  groups, currency, open, setOpen,
}: {
  groups: SpendGroup[]; currency: string; open: Set<string>; setOpen: (s: Set<string>) => void;
}) {
  const opened = [...open][0] ?? null;
  const active = opened ? groups.find((g) => g.name === opened) : null;

  if (active) {
    // раскрытая группа → плитки её категорий
    const tot = active.total || 1;
    return (
      <div>
        <button onClick={() => setOpen(new Set())} className="caption" style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: theme.colors.sub, marginBottom: 8, padding: 0 }}>
          ← все группы
        </button>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, minHeight: 220 }}>
          {active.cats.map((c, i) => {
            const flex = Math.max(14, Math.round((c.total / tot) * 100));
            return (
              <div key={c.name} style={tile(TINTS[i % TINTS.length], flex)}>
                {c.name}<br /><span className="num" style={{ fontSize: 13 }}>{fmtK(c.total)}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  const tot = groups.reduce((s, g) => s + g.total, 0) || 1;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, minHeight: 210 }}>
      {groups.map((g, i) => {
        const flex = Math.max(16, Math.round((g.total / tot) * 100));
        return (
          <div key={g.name} onClick={() => setOpen(new Set([g.name]))} style={{ ...tile(TINTS[i % TINTS.length], flex), cursor: 'pointer' }}>
            {g.name}<br /><span className="num" style={{ fontSize: 15 }}>{fmtMoney(g.total, currency)}</span>
          </div>
        );
      })}
    </div>
  );
}

function tile(bg: string, flexBasisPct: number): CSSProperties {
  const light = flexBasisPct < 24;
  return {
    flex: `1 1 ${flexBasisPct}%`,
    minHeight: 64,
    background: bg,
    color: light ? theme.colors.ink : theme.colors.paper,
    padding: 10,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    fontSize: 13,
    lineHeight: 1.2,
  };
}

/** Лимиты — бары 5px (вывод-резюме живёт в строке-оглавлении): проблемные, по тапу раскрыть все. */
function HealthLimits({ groups }: { groups: SpendGroup[] }) {
  const { viewData } = useBudget();
  const [showAll, setShowAll] = useState(false);
  const [planCat, setPlanCat] = useState<string | null>(null);
  const cats = groups.flatMap((g) => g.cats).filter((c) => c.plan > 0);
  if (!cats.length) return <div style={{ color: theme.colors.sub, fontSize: 13 }}>Лимиты не заданы. Задай план категориям в разделе «Категории».</div>;

  const allCats = viewData?.categories ?? [];

  const over = cats.filter((c) => c.total > c.plan);
  const near = cats.filter((c) => c.total <= c.plan && c.total >= c.plan * 0.85);
  const attention = [...over, ...near].sort((a, b) => b.total / b.plan - a.total / a.plan);
  const shown = showAll ? cats.slice().sort((a, b) => b.total / b.plan - a.total / a.plan) : attention;
  const healthy = cats.length - attention.length;

  return (
    <div>
      {shown.map((c) => {
        const ratio = c.plan > 0 ? c.total / c.plan : 0;
        const isOver = c.total > c.plan;
        const fullCat = allCats.find((ac) => ac.name === c.name);
        return (
          <div key={c.name} style={{ borderTop: `1px solid ${theme.colors.line}`, padding: '10px 0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
              <span style={{ fontSize: 15, display: 'flex', alignItems: 'center', gap: 7 }}>
                <Icon name={categoryIconName({ name: c.name })} size={14} color={theme.colors.sub} /> {c.name}
              </span>
              <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <span className="num" style={{ fontSize: 13, color: isOver ? theme.colors.accent : theme.colors.sub }}>
                  {isOver ? `перебор ${fmtK(c.total - c.plan)}` : `осталось ${fmtK(c.plan - c.total)}`}
                </span>
                {fullCat && (
                  <button
                    onClick={() => setPlanCat((prev) => (prev === c.name ? null : c.name))}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      borderBottom: `1px dashed ${theme.colors.subSoft}`,
                      padding: '0 1px 1px',
                      fontSize: 12,
                      color: theme.colors.sub,
                      cursor: 'pointer',
                      fontFamily: theme.font,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 2,
                    }}
                  >
                    план<Icon name="arrowRight" size={10} inline />
                  </button>
                )}
              </span>
            </div>
            <div style={{ position: 'relative', height: 5, background: theme.colors.line }}>
              <div style={{ width: `${Math.min(100, ratio * 100)}%`, height: '100%', background: isOver ? theme.colors.accent : theme.colors.ink }} className="bar" />
            </div>
            {planCat === c.name && fullCat && (
              <div style={{ marginTop: 10 }}>
                <CategoryPlanPad
                  category={fullCat}
                  cats={allCats}
                  onClose={() => setPlanCat(null)}
                />
              </div>
            )}
          </div>
        );
      })}

      {!showAll && healthy > 0 && (
        <button
          onClick={() => setShowAll(true)}
          style={{ width: '100%', marginTop: 12, padding: '10px 0', background: 'transparent', border: `1px solid ${theme.colors.line}`, borderRadius: theme.radius.sm, color: theme.colors.sub, fontFamily: theme.font, fontSize: 13, cursor: 'pointer' }}
        >
          {healthy} {plural(healthy, 'категория в норме', 'категории в норме', 'категорий в норме')} — показать все
        </button>
      )}
    </div>
  );
}

function plural(n: number, a: string, b: string, c: string): string {
  const m10 = n % 10, m100 = n % 100;
  if (m100 >= 11 && m100 <= 14) return c;
  if (m10 === 1) return a;
  if (m10 >= 2 && m10 <= 4) return b;
  return c;
}

// ---------------------------------------------------------------------------
// НАКОПЛЕНИЯ — компоненты
// ---------------------------------------------------------------------------


function MoMBars({
  cur,
  prev,
  curLabel,
  prevLabel,
  currency,
}: {
  cur: number;
  prev: number;
  curLabel: string;
  prevLabel: string;
  currency: string;
}) {
  const max = Math.max(cur, prev, 1);
  const delta = prev > 0 ? ((cur - prev) / prev) * 100 : null;
  const rows = [
    { label: curLabel, v: cur, color: theme.colors.ink },
    { label: prevLabel, v: prev, color: theme.colors.subSoft },
  ];
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      {rows.map((r) => (
        <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 32, fontSize: 12, color: theme.colors.sub }}>{r.label}</span>
          <div style={{ flex: 1, height: 16, background: theme.colors.line, borderRadius: 0 }}>
            <div
              className="bar"
              style={{
                width: `${(r.v / max) * 100}%`,
                height: '100%',
                background: r.color,
                borderRadius: 0,
              }}
            />
          </div>
          <span style={{ width: 70, fontSize: 12, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(r.v, currency)}</span>
        </div>
      ))}
      {delta !== null && (
        <div style={{ fontSize: 13, marginTop: 2, color: delta <= 0 ? theme.colors.green : theme.colors.accent }}>
          {delta <= 0 ? '−' : '+'}{Math.abs(delta).toFixed(0)}% к прошлому месяцу
          {delta <= 0 ? ' — темп лучше' : ''}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Стопки месяцев
// ---------------------------------------------------------------------------

function Stacked({ months, colors }: { months: ReturnType<typeof buildStacked>; colors: Map<string, string> }) {
  const H = 150;
  const PAD_B = 16;
  const max = Math.max(...months.map((m) => m.total), 1);
  const barW = 36;
  const step = W / months.length;

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: 'auto', display: 'block' }}>
      {months.map((m, i) => {
        const cx = step * i + step / 2;
        const isCurrent = i === months.length - 1;
        let y = H - PAD_B;
        // крупные группы снизу — тёмный тинт у основания стопки
        const segments = [...m.segments].sort((a, b) => b.value - a.value);
        return (
          <g key={m.key}>
            {segments.map((s, j) => {
              const h = (s.value / max) * (H - PAD_B - 18);
              y -= h;
              return <rect key={j} x={cx - barW / 2} y={y} width={barW} height={h} fill={colors.get(s.name) ?? theme.colors.inkTints[3]} />;
            })}
            {m.total > 0 && (
              <text x={cx} y={y - 4} fontSize={9} fill={theme.colors.sub} textAnchor="middle" fontFamily={theme.font}>
                {fmtK(m.total)}
              </text>
            )}
            <text x={cx} y={H - 3} fontSize={10} fill={isCurrent ? theme.colors.ink : theme.colors.sub} textAnchor="middle" fontFamily={theme.font}>
              {m.label}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

function Legend({ colors }: { colors: Map<string, string> }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 12px', justifyContent: 'center', marginTop: 8 }}>
      {Array.from(colors.entries()).map(([name, color]) => (
        <span key={name} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, color: theme.colors.sub }}>
          <span style={{ width: 9, height: 9, background: color }} />
          {name}
        </span>
      ))}
    </div>
  );
}

